import { APIService } from 'api/APIService'
import { RequestError } from 'api/errors'
import type { AnyPage } from 'api/api.types'
import type { StoreType } from 'src/store'
import { getSalons, getServices, getSettings, setSalons, setServices, setSettings } from 'src/store/data.store'
import {
  getPage,
  getSelectedCityId,
  setIsPageLoading,
  setPage,
  setSelectedCityId,
} from 'src/store/app.state.store'
import { Storage } from 'utils/storage'

const UTM_TTL = 24 * 60 * 60 * 1000
const LOADER_DELAY = 150

/** Loads settings, salons and services once; picks the city (cookie, saved, or the first one) */
export const initApp = async (store: StoreType, cityId?: number): Promise<void> => {
  const state = store.getState()
  const promises: Array<Promise<void>> = []

  if (!getSalons(state).length) {
    promises.push(APIService.data.fetchSalons().then((salons) => {
      store.dispatch(setSalons(salons))
    }))
  }

  if (!getServices(state).length) {
    promises.push(APIService.data.fetchServices().then((services) => {
      store.dispatch(setServices(services))
    }))
  }

  const settings = getSettings(state)
  if (!settings || !Object.values(settings).length) {
    promises.push(APIService.data.fetchSettings().then((loaded) => {
      store.dispatch(setSettings(loaded))
      const initialCityId = cityId ?? getSelectedCityId(store.getState()) ?? loaded.cities[0]?.city.id
      if (loaded.cities.some(({ city }) => city.id === initialCityId)) {
        store.dispatch(setSelectedCityId(initialCityId))
      }
    }))
  }

  await Promise.all(promises)
}

const saveUtm = (search: string) => {
  const utm: Record<string, string> = {}
  new URLSearchParams(search).forEach((value, key) => {
    if (key.startsWith('utm')) {
      utm[key] = value
    }
  })
  if (Object.keys(utm).length > 0) {
    Storage.setItem('utm', utm, Date.now() + UTM_TTL)
  }
}

/** A CMS redirect for this path, as a page the router renders */
const findRedirect = (store: StoreType, path: string): AnyPage | null => {
  const redirect = getSettings(store.getState())?.redirects.find((r) => r.url === path)
  if (!redirect) {
    return null
  }
  return {
    template: 'redirect',
    status: redirect.status,
    to: redirect.linkedPage.fullPath,
  }
}

/** A salon page switches the selected city to the salon's city */
const applyPage = (store: StoreType, page: AnyPage) => {
  if (page.template === 'salon-page') {
    const salonCityId = page.templateVars?.pageVars?.salon?.cityId
    if (salonCityId) {
      store.dispatch(setSelectedCityId(salonCityId))
    }
  }
  store.dispatch(setPage(page))
}

let loadingDebounceTimer: ReturnType<typeof setTimeout> | undefined
let prevPath: string | undefined

/** Browser: loads the page for a path, showing the loader only if it takes over 150 ms */
export const initPage = async (store: StoreType, path: string, search?: string): Promise<void> => {
  if (search) {
    saveUtm(search)
  }

  const currentPage = getPage(store.getState())
  if (prevPath === path || (currentPage && 'fullPath' in currentPage && currentPage.fullPath === path)) {
    return
  }
  prevPath = path
  clearTimeout(loadingDebounceTimer)
  loadingDebounceTimer = setTimeout(() => {
    store.dispatch(setIsPageLoading(true))
  }, LOADER_DELAY)

  try {
    const redirect = findRedirect(store, path)
    applyPage(store, redirect ?? await APIService.data.fetchPage(path))
  } catch {
    store.dispatch(setPage({ fullPath: path }))
  } finally {
    clearTimeout(loadingDebounceTimer)
    store.dispatch(setIsPageLoading(false))
  }
}

/** Reloads the current page, e.g. after the city changes */
export const refreshPage = async (store: StoreType): Promise<void> => {
  const currentPage = getPage(store.getState())
  if (currentPage && 'fullPath' in currentPage && currentPage.fullPath) {
    store.dispatch(setPage(await APIService.data.fetchPage(currentPage.fullPath)))
  }
}

/** Server: loads the page for a path and rethrows API errors so SSR can set the status */
export const initPageForSSR = async (store: StoreType, path: string): Promise<void> => {
  try {
    const redirect = findRedirect(store, path)
    applyPage(store, redirect ?? await APIService.data.fetchPage(path))
  } catch (e) {
    if (e instanceof RequestError && e.code === 'PAGE_NOT_FOUND') {
      store.dispatch(setPage({ fullPath: path }))
    } else {
      store.dispatch(setPage(null))
    }
    throw e
  }
}
